import * as React from 'react'

import { clsx } from 'clsx'

import { Dialog } from '@buffer-mono/popcorn'

import styles from './ComposerFrame.module.css'

type ComposerFrameState = 'default' | 'maximized'

const ComposerFrameContext = React.createContext<ComposerFrameState>('default')

type ComposerFrameContentProps = Omit<
  React.ComponentPropsWithoutRef<typeof Dialog.Content>,
  'size'
> & {
  state?: ComposerFrameState
}

const Content = React.forwardRef<
  React.ElementRef<typeof Dialog.Content>,
  ComposerFrameContentProps
>(({ state = 'default', className, children, ...props }, forwardedRef) => (
  <ComposerFrameContext.Provider value={state}>
    <Dialog.Content
      {...props}
      ref={forwardedRef}
      size={state === 'maximized' ? 'maximize' : undefined}
      className={clsx(
        styles.content,
        state === 'maximized' && styles.maximized,
        className,
      )}
    >
      {children}
    </Dialog.Content>
  </ComposerFrameContext.Provider>
))

Content.displayName = 'ComposerFrame.Content'

type ComposerFrameHeaderProps = React.ComponentPropsWithoutRef<
  typeof Dialog.Header
>

const Header = React.forwardRef<
  React.ElementRef<typeof Dialog.Header>,
  ComposerFrameHeaderProps
>(({ className, children, ...props }, forwardedRef) => {
  const state = React.useContext(ComposerFrameContext)

  return (
    <Dialog.Header
      {...props}
      ref={forwardedRef}
      className={clsx(styles.header, className)}
      data-maximized={state === 'maximized' || undefined}
    >
      {children}
    </Dialog.Header>
  )
})

Header.displayName = 'ComposerFrame.Header'

type ComposerFrameBodyProps = React.ComponentPropsWithoutRef<typeof Dialog.Body>

const Body = React.forwardRef<
  React.ElementRef<typeof Dialog.Body>,
  ComposerFrameBodyProps
>(({ className, children, ...props }, forwardedRef) => {
  const state = React.useContext(ComposerFrameContext)

  return (
    <Dialog.Body
      {...props}
      ref={forwardedRef}
      className={clsx(
        styles.body,
        state === 'maximized' && styles.maximized,
        className,
      )}
    >
      {children}
    </Dialog.Body>
  )
})

Body.displayName = 'ComposerFrame.Body'

type ComposerFrameFooterProps = React.ComponentPropsWithoutRef<
  typeof Dialog.Footer
>

const Footer = React.forwardRef<
  React.ElementRef<typeof Dialog.Footer>,
  ComposerFrameFooterProps
>(({ className, children, showSeparator = false, ...props }, forwardedRef) => (
  <Dialog.Footer
    {...props}
    ref={forwardedRef}
    showSeparator={showSeparator}
    className={clsx(styles.footer, className)}
  >
    {children}
  </Dialog.Footer>
))

Footer.displayName = 'ComposerFrame.Footer'

export const ComposerFrame = {
  Content,
  Header,
  Body,
  Footer,
}
